import React, { useCallback, useEffect, useRef, useState } from "react";
import { Radio, RefreshCw, X } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

/**
 * AndreasRadio
 *
 * A small floating card that shows ONE fresh Cyprus micro-observation
 * from Andreas on every page load. Bottom-left, out of the way of the
 * chat launcher (bottom-right) and the countdown ribbon.
 *
 * Behaviour:
 *  - Fetches once on mount when the guest is authed
 *  - Manual refresh button rolls a new fact
 *  - Dismiss hides for the rest of the session (per-tab, not persisted)
 *  - Hidden on the /admin route (checked by Layout mount location)
 */

const COPY = {
  en: { kicker: "Andreas radio", refresh: "New" },
  fr: { kicker: "Radio Andreas", refresh: "Autre" },
  el: { kicker: "Ραδιόφωνο Andreas", refresh: "Άλλο" },
  pl: { kicker: "Radio Andreasa", refresh: "Nowe" },
};

const SESSION_HIDE_KEY = "em_andreas_radio_hidden";

const AndreasRadio = ({ variant = "inline" }) => {
  const { lang } = useLang();
  const { isAuthed, authReady } = useAuth();
  const c = COPY[lang] || COPY.en;
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(() => {
    try { return sessionStorage.getItem(SESSION_HIDE_KEY) === "1"; } catch { return false; }
  });
  const fetchedForLang = useRef(null);

  const fetchFact = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/andreas/radio?lang=${lang}`);
      if (data && data.text) setText(data.text);
    } catch {
      /* silent — Andreas is off-mic */
    } finally {
      setBusy(false);
    }
  }, [lang]);

  useEffect(() => {
    if (!authReady || !isAuthed) return;
    if (hidden) return;
    if (fetchedForLang.current === lang) return;
    fetchedForLang.current = lang;
    fetchFact();
  }, [authReady, isAuthed, hidden, lang, fetchFact]);

  const dismiss = () => {
    setHidden(true);
    try { sessionStorage.setItem(SESSION_HIDE_KEY, "1"); } catch { /* ignore */ }
  };

  if (!authReady || !isAuthed) return null;
  if (hidden) return null;
  if (!text) return null;

  // Inline card variant — sits in the page flow, doesn't cover anything.
  return (
    <div
      className="mx-auto max-w-[1180px] px-5 sm:px-8"
      data-testid="andreas-radio"
    >
      <div className="relative rounded-2xl border border-border bg-background/70 backdrop-blur px-4 sm:px-5 py-4 flex items-start sm:items-center gap-3 sm:gap-4">
        <span className="relative w-10 h-10 shrink-0 rounded-full bg-accent/15 text-accent inline-flex items-center justify-center">
          <Radio className="w-4 h-4" />
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-accent animate-pulse" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="editorial-kicker text-[9px] text-foreground/55">{c.kicker}</p>
          <p
            className="font-serif-display italic text-[15px] sm:text-[17px] leading-snug text-foreground/85 mt-1"
            data-testid="andreas-radio-text"
          >
            {text}
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={fetchFact}
            disabled={busy}
            aria-label={c.refresh}
            title={c.refresh}
            data-testid="andreas-radio-refresh"
            className="w-8 h-8 rounded-md text-accent hover:bg-accent/10 disabled:opacity-50 inline-flex items-center justify-center"
          >
            <RefreshCw className={"w-4 h-4 " + (busy ? "animate-spin" : "")} />
          </button>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            data-testid="andreas-radio-dismiss"
            className="w-8 h-8 rounded-md text-foreground/50 hover:text-foreground hover:bg-foreground/5 inline-flex items-center justify-center"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AndreasRadio;
